import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import { AGENTS, AGENT_CATEGORIES } from "../data/agents";
import { agentForMatiere, matiereFromTitle } from "../data/matiere-to-agent";
import { PLANS_CATALOG } from "../lib/plansCatalog";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
// AgentsWidget removed — replaced by Skills Engine
import HomePage from "./HomePage"; // eager : premier écran (Accueil)
import JobsList from "../components/JobsList";
import { colors as C, fonts as F, cab as K, cabMatiere } from "../theme";
import { useIsMobile, useIsCompact } from "../hooks/useIsMobile";
import { IAvocatMark } from "../components/IAvocatLogo";
// Chrome « E / Clarté » : barre de navigation HORIZONTALE en haut (remplace la
// sidebar encre). Sidebar.jsx / Topbar.jsx restent en place (nettoyage au lot
// final) mais ne sont plus rendus.
import TopNav from "../components/chrome/TopNav";
import { useToast } from "../components/Toast";
import { setDarkMode as setHtmlTheme, initDarkModeFromStorage } from "../theme";

// ─── Code-splitting (E-LOT 5b) ───
// Écrans internes lourds chargés à la demande (React.lazy + Suspense). Le chrome
// (top-nav, bottom-nav mobile) reste rendu en synchrone ; seul le corps est lazy.
// HomePage reste eager (1er écran). AgendaPage est défini dans ce module (interne).
const ChatPage = lazy(() => import("./ChatPage"));
const TeamPage = lazy(() => import("./TeamPage"));
const JurisprudencePage = lazy(() => import("./JurisprudencePage"));
const ActeEditorPage = lazy(() => import("./ActeEditorPage"));
const DossiersPage = lazy(() => import("../components/dashboard/DossiersPanel"));
const EmailsPage = lazy(() => import("../components/dashboard/EmailsPanel"));
const SettingsPage = lazy(() => import("../components/dashboard/SettingsPanel"));
const SupportPage = lazy(() => import("../components/dashboard/SupportPanel"));

// Shared helpers (extracted)
import {
  PLANS, sbGet, sbCount, sbUpsert, bootstrapDashboard, syncSbHeaders, syncDarkMode,
  Ic, classifyEvent, DBX_APP_KEY, handleDropboxCallback
} from "../lib/dashboardHelpers";



// ─── Module-level helpers (from original monolith) ───
const Hov = ({ children, style, hStyle, ...p }) => {
  const [h, setH] = useState(false);
  return <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ ...style, ...(h ? hStyle : {}) }} {...p}>{children}</div>;
};

// ════════════════════════════════════════════════════════════════════════
//  AGENDA — « LA LIGNE DE VIE DU CABINET » (socle « Le Cabinet »)
//  Réf. visuelle EXACTE : design-mockups/concepts-v2/le-cabinet/agenda.html
//  Ce n'est PAS un calendrier-grille : un fil chronologique vertical des
//  échéances, mis en scène par CRITICITÉ (J-x, alerte bordeaux) et
//  ANTICIPATION (le collaborateur référent prépare l'échéance).
//  Helpers locaux (icônes, criticité, jalon de jour) — données 100 % réelles
//  (calendar_events). Aucune donnée de préparation inventée : cf. notes plus bas.
// ════════════════════════════════════════════════════════════════════════

// Icônes de fil — stroke currentColor (héritent de la couleur du parent).
const AgIc = {
  doc:      (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M6 2h8l4 4v16H6z" stroke="currentColor" strokeWidth="1.6" /><path d="M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>),
  gavel:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  alert:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  user:     (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.7" /><path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  expertise:(p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 21s-7-4.5-7-10a7 7 0 0 1 14 0c0 5.5-7 10-7 10z" stroke="currentColor" strokeWidth="1.7" /><path d="M9 11h6M12 8v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  handshake:(p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 12l4-4 5 4 5-4 4 4-4 4-5-4-5 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>),
  clock:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" /><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  cal:      (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  check:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  arrow:    (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  list:     (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  shield:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.6" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  folder:   (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 7l5 0 2-3h4l2 3 5 0v13a1 1 0 01-1 1H4a1 1 0 01-1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>),
};

// Icône de la pastille (pin) selon la catégorie d'événement (classifyEvent).
const AG_PIN_ICON = {
  audience: AgIc.gavel, urgence: AgIc.alert, deadline: AgIc.alert,
  rdv: AgIc.user, expertise: AgIc.expertise, mediation: AgIc.handshake,
  formation: AgIc.doc, personnel: AgIc.user, autre: AgIc.cal,
};

// Criticité d'une échéance, dérivée du DÉLAI réel (jours restants) + nature.
// → bordeaux plein (urgent/forclusion) quand l'échéance « dure » (audience,
//   dépôt, délai, urgence) tombe à J-3 ou moins ; « soon » (or) sous ~7 j ;
//   « calm » au-delà. Honnête : 100 % temporel, aucune donnée inventée.
function agCriticity(ev, now) {
  if (!ev?.date) return { level: "calm", code: "—", isCritical: false };
  const diffDays = Math.ceil((ev.date - now) / 86400000);
  const isToday = ev.date.toDateString() === now.toDateString();
  const tom = new Date(now); tom.setDate(tom.getDate() + 1);
  const isTomorrow = ev.date.toDateString() === tom.toDateString();
  const hard = ev._cat === "audience" || ev._cat === "deadline" || ev._cat === "urgence";
  const code = isToday ? "Aujourd'hui" : isTomorrow ? "Demain · J-1" : diffDays <= 0 ? "Aujourd'hui" : `J-${diffDays}`;
  // Échéances « dures » (forclusion / audience / dépôt) : alerte pleine sous 3 j.
  if (hard && diffDays <= 3) return { level: "urgent", code, isCritical: true, diffDays };
  // Tout ce qui tombe sous ~7 j : « soon » (or) ; au-delà : « calm ».
  if (diffDays <= 7) return { level: "soon", code, isCritical: false, diffDays };
  return { level: "calm", code, isCritical: false, diffDays };
}

// Libellé de jalon de jour sur le fil (en-tête de groupe).
function agDayLabel(date, now) {
  const isToday = date.toDateString() === now.toDateString();
  const tom = new Date(now); tom.setDate(tom.getDate() + 1);
  const isTomorrow = date.toDateString() === tom.toDateString();
  const diffDays = Math.ceil((date - now) / 86400000);
  const dd = date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const ddCap = dd.charAt(0).toUpperCase() + dd.slice(1);
  let rel;
  if (isToday) rel = "Aujourd'hui";
  else if (isTomorrow) rel = "Demain · J-1";
  else if (diffDays > 1 && diffDays <= 7) rel = `Cette semaine · J-${diffDays}`;
  else if (diffDays > 7) rel = `J-${diffDays}`;
  else rel = "";
  return { ddCap, rel, isToday };
}



// ─── Fallback Suspense « E » (code-splitting) ───
// État de chargement discret et calme (langage E) : un peu de blanc, la marque
// IAvocat estompée + un libellé mono sobre. PAS de gros spinner. Sert de fallback
// pour tous les écrans lazy-loadés (corps de page). `full` = pleine hauteur (chat
// / éditeur d'acte qui occupent tout l'écran), sinon hauteur de zone de contenu.
function PageFallback({ full = false }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: full ? "100%" : "60vh",
        width: "100%",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{ display: "inline-flex", marginBottom: 12, opacity: 0.55, animation: "pulse 1.6s ease-in-out infinite" }}>
          <IAvocatMark size={26} />
        </div>
        <div style={{ fontFamily: F.mono, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textMuted }}>
          Chargement
        </div>
      </div>
    </div>
  );
}

// ============================================================
// OTHER PAGES (compact)
// ============================================================

// ─── AgendaPage — « LA LIGNE DE VIE DU CABINET » ───
function AgendaPage({ lawyer, isMobile, dossiers, onAssignToAgent }) {
  const toast = useToast();
  const now = new Date();
  const [view, setView] = useState("semaine");
  const [calEvents, setCalEvents] = useState(null);
  const [loadingCal, setLoadingCal] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [assignModal, setAssignModal] = useState(null); // { event, dossier, agent, instructions, proposalId? }
  const [createDossierModal, setCreateDossierModal] = useState(null); // { source_type, source_id, source_title, client_name, matiere }
  const [creatingDossier, setCreatingDossier] = useState(false);
  // Propositions d'anticipation (cron scan-audiences → agent_proposals).
  // RLS lawyers_own_proposals : l'avocat lit/répond sur SES propositions.
  const [proposals, setProposals] = useState(null);

  // Index des dossiers par id pour résolution rapide (matiere, client_name, dossier_ref)
  const dossierById = useMemo(() => {
    const m = new Map();
    for (const d of (dossiers || [])) m.set(d.id, d);
    return m;
  }, [dossiers]);

  useEffect(() => {
    async function fetchCalendar() {
      setLoadingCal(true);
      try {
        if (!lawyer?.id) { setCalEvents([]); setLoadingCal(false); return; }
        // Fenêtre : de maintenant - 1 jour à +60 jours
        const from = new Date(Date.now() - 86400000).toISOString();
        const to = new Date(Date.now() + 60 * 86400000).toISOString();
        const rows = await sbGet(
          `/calendar_events?lawyer_id=eq.${lawyer.id}` +
          `&start_at=gte.${from}&start_at=lte.${to}` +
          `&order=start_at.asc&select=id,title,description,start_at,end_at,location,dossier_id,status&limit=200`
        );
        const mapped = (rows || []).map(ev => ({
          id: ev.id,
          summary: ev.title || '',
          description: ev.description || '',
          location: ev.location || '',
          dossierId: ev.dossier_id,
          start: ev.start_at,
          end: ev.end_at,
          date: new Date(ev.start_at),
          endDate: ev.end_at ? new Date(ev.end_at) : null,
        }));
        setCalEvents(mapped);
      } catch(e) { console.error("Calendar fetch error:", e); setCalEvents([]); }
      setLoadingCal(false);
    }
    fetchCalendar();
  }, [lawyer?.id]);

  // Propositions d'anticipation des audiences (status pending/approved —
  // approved reste visible tant que le chat n'a pas été lancé : rien ne se
  // perd si l'avocat referme la modale). Filtre source_workflow : seules les
  // propositions du cron scan-audiences ont le payload attendu ici.
  useEffect(() => {
    async function fetchProposals() {
      if (!lawyer?.id) { setProposals([]); return; }
      const rows = await sbGet(
        `/agent_proposals?lawyer_id=eq.${lawyer.id}` +
        `&proposal_type=eq.brief_audience&source_workflow=eq.cron-scan-audiences` +
        `&status=in.(pending,approved)&order=expires_at.asc&limit=6` +
        `&select=id,title,status,action_payload,expires_at,created_at`
      );
      setProposals(rows || []);
    }
    fetchProposals();
  }, [lawyer?.id]);

  // Transition de statut d'une proposition (RLS : lawyer_id = avocat courant).
  // rejected / executed sortent de la liste ; approved y reste (en attente du
  // lancement effectif dans le chat).
  const respondToProposal = useCallback(async (id, status) => {
    setProposals(prev => (prev || [])
      .map(p => (p.id === id ? { ...p, status } : p))
      .filter(p => p.status === 'pending' || p.status === 'approved'));
    const nowIso = new Date().toISOString();
    const patch = { status };
    if (status === 'approved' || status === 'rejected') patch.responded_at = nowIso;
    if (status === 'executed') patch.executed_at = nowIso;
    const { error } = await supabase.from('agent_proposals').update(patch).eq('id', id);
    if (error) console.error('[AgendaPage] agent_proposals update:', error.message);
  }, []);

  // Accepter une proposition : approved immédiatement, puis ouverture de la
  // modale « Confier » pré-remplie (référent + action suggérée). 'executed'
  // n'est posé qu'au lancement réel du chat (bouton Confier de la modale).
  const openProposalModal = (p) => {
    const pl = p.action_payload || {};
    const realEv = (calEvents || []).find(e => e.id === pl.calendar_event_id);
    const ev = realEv || {
      id: pl.calendar_event_id || p.id,
      summary: pl.event_title || p.title || '',
      description: '',
      location: pl.event_location || '',
      dossierId: pl.dossier_id || null,
      start: pl.event_start_at,
      end: pl.event_end_at || null,
      date: pl.event_start_at ? new Date(pl.event_start_at) : null,
      endDate: pl.event_end_at ? new Date(pl.event_end_at) : null,
    };
    const dossier = (pl.dossier_id && dossierById.get(pl.dossier_id)) || (ev.dossierId && dossierById.get(ev.dossierId)) || null;
    const matiereKey = dossier?.matiere || pl.matiere || matiereFromTitle(ev.summary) || null;
    const agent = agentForMatiere(matiereKey)
      || (pl.suggested_agent_id && AGENTS.find(a => a.id === pl.suggested_agent_id))
      || null;
    const instructions = (pl.suggested_actions || []).join('. ');
    if (p.status === 'pending') respondToProposal(p.id, 'approved');
    setAssignModal({ event: ev, dossier, agent, instructions, proposalId: p.id });
  };

  // Classify events
  const events = (calEvents || []).map(ev => {
    const cls = classifyEvent(ev.summary);
    return { ...ev, _cat: cls.category, _color: cls.color, _catIcon: cls.icon, _catLabel: cls.label };
  });
  const isDemo = !calEvents;
  const todayStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Category filter
  const [catFilter, setCatFilter] = useState("all");

  // La couleur d'événement provient de classifyEvent (palette catégorielle, data).

  const weekEvents = events.filter(e => e.date && (e.date - now) / 86400000 <= 7 && e.date >= now);
  const monthEvents = events.filter(e => e.date && (e.date - now) / 86400000 <= 30 && e.date >= new Date(now.getFullYear(), now.getMonth(), now.getDate()));

  // Horizon temporel (remplace jour/semaine/mois) → réutilise les ensembles
  // calculés. « semaine » = ≤7 j · « dix » = ≤10 j · « mois » = ≤30 j.
  const tenDayEvents = events.filter(e => e.date && (e.date - now) / 86400000 <= 10 && e.date >= now);
  const horizonRaw = view === "mois" ? monthEvents : view === "dix" ? tenDayEvents : weekEvents;

  // Rattachement : chaque échéance porte sa matière réelle (dossier → cabMatiere,
  // sinon dérivée du titre). Sert au filtre par matière ET aux accents de carte.
  const decorate = (e) => {
    const dossier = e.dossierId ? dossierById.get(e.dossierId) : null;
    const matiereKey = dossier?.matiere || matiereFromTitle(e.summary) || null;
    return { ...e, _dossier: dossier, _matiereKey: matiereKey, _m: cabMatiere(matiereKey) };
  };
  const horizonEvents = horizonRaw.map(decorate);

  // Filtre par matière (clé `client_dossiers.matiere`, normalisée).
  const norm = (s) => String(s || "").toLowerCase().trim();
  const matterFilterEvents = catFilter === "all" ? horizonEvents : horizonEvents.filter(e => norm(e._matiereKey) === catFilter);

  // Matières présentes dans l'horizon courant → chips de légende (pas de chip vide).
  const presentMatters = [];
  const seenM = new Set();
  for (const e of horizonEvents) {
    const k = norm(e._matiereKey);
    if (k && !seenM.has(k)) { seenM.add(k); presentMatters.push(k); }
  }

  // Compteurs d'imminence (réels) : échéances « dures » critiques (J-3 ou moins).
  const criticalEvents = horizonEvents
    .map(e => ({ e, c: agCriticity(e, now) }))
    .filter(x => x.c.isCritical)
    .sort((a, b) => (a.c.diffDays ?? 99) - (b.c.diffDays ?? 99));
  const tenDayCount = tenDayEvents.length;

  // Regroupement par JOUR (jalons du fil), sur les événements filtrés.
  const byDay = [];
  let lastKey = null;
  for (const e of matterFilterEvents) {
    if (!e.date) continue;
    const key = e.date.toDateString();
    if (key !== lastKey) { byDay.push({ key, date: e.date, items: [] }); lastKey = key; }
    byDay[byDay.length - 1].items.push(e);
  }

  const HORIZONS = [
    { id: "semaine", label: "Cette semaine" },
    { id: "dix", label: "10 jours" },
    { id: "mois", label: "Le mois" },
  ];
  const nowTime = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const dateCap = todayStr.charAt(0).toUpperCase() + todayStr.slice(1);
  const PAD = isMobile ? "20px 16px 28px" : "30px 30px 36px";

  return <>
    {/* Surface ivoire « Le Cabinet » — peinte localement (ne touche pas --bg-page). */}
    <div style={{ fontFamily: F.sans, color: K.ink, paddingTop: isMobile ? 8 : 16 }}>
      <div style={{
        background: `radial-gradient(120% 80% at 100% 0%, rgba(122,31,46,.035), transparent 60%), ${K.paper}`,
        borderRadius: K.rXl,
        border: `1px solid ${K.hair}`,
        padding: PAD,
        boxShadow: K.lift1,
      }}>
    {/* ════════ EN-TÊTE : la promesse de veille ════════ */}
    <div style={{ fontFamily: F.monoCab, fontSize: 11.5, letterSpacing: ".22em", textTransform: "uppercase", color: K.ink3, marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span aria-hidden="true" style={{ width: 26, height: 1, background: K.bordeaux }} />
      Agenda · La ligne de vie du cabinet · {dateCap}
    </div>

    {/* Bandeau « veille des délais » — ton sombre console (jumeau du débrief) */}
    <div style={{
      position: "relative", borderRadius: K.rXl, overflow: "hidden", color: "#F6ECEE",
      background: "radial-gradient(125% 130% at 6% 0%, rgba(168,50,74,.40), transparent 56%), radial-gradient(90% 120% at 100% 100%, rgba(46,114,184,.12), transparent 60%), linear-gradient(160deg, #3a1620, #271017)",
      boxShadow: "0 30px 70px -34px rgba(78,22,32,.85)", marginBottom: 22,
    }}>
      <div style={{ padding: isMobile ? "22px 20px" : "30px 34px 28px", display: "flex", gap: isMobile ? 18 : 30, alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ flex: 1, minWidth: isMobile ? 0 : 300 }}>
          <div style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: ".18em", textTransform: "uppercase", color: "rgba(255,190,202,.95)", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "#5BE0A6", boxShadow: "0 0 0 3px rgba(91,224,166,.22)", animation: "cabBlink 2.6s infinite" }} />
            Veille des délais — Sophie surveille pour vous
          </div>
          <h1 style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: isMobile ? 24 : "clamp(25px,3.1vw,38px)", lineHeight: 1.16, letterSpacing: "-.02em", color: "#fff", maxWidth: "26ch", margin: 0 }}>
            {isDemo ? (
              <>Aucun délai ne vous échappera. Connectez votre calendrier : le cabinet <em style={{ fontStyle: "italic", color: K.gildHi }}>veille sur vos échéances</em>.</>
            ) : criticalEvents.length > 0 ? (
              <>Aucun délai ne vous échappera. <b style={{ fontWeight: 600, color: "#fff" }}>{criticalEvents.length} échéance{criticalEvents.length > 1 ? "s" : ""} critique{criticalEvents.length > 1 ? "s" : ""}</b>{" "}
                {view === "mois" ? "ce mois" : view === "dix" ? "sous 10 jours" : "cette semaine"} — <em style={{ fontStyle: "italic", color: K.gildHi }}>chacune a son référent</em>.</>
            ) : horizonEvents.length > 0 ? (
              <>L'horizon est <b style={{ fontWeight: 600, color: "#fff" }}>dégagé</b> — {horizonEvents.length} échéance{horizonEvents.length > 1 ? "s" : ""} suivie{horizonEvents.length > 1 ? "s" : ""}, rien d'urgent.</>
            ) : (
              <>Aucune échéance {view === "mois" ? "ce mois" : view === "dix" ? "sous 10 jours" : "cette semaine"}. <b style={{ fontWeight: 600, color: "#fff" }}>L'horizon est dégagé.</b></>
            )}
          </h1>
          <div style={{ marginTop: 16, fontSize: 13.5, color: "rgba(246,236,238,.72)", display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", lineHeight: 1.5 }}>
            <span style={{ fontFamily: F.serifDisplay, fontStyle: "italic", color: K.gildHi, fontWeight: 500 }}>— Vous tranchez, le cabinet exécute.</span>
            {criticalEvents.length > 0 ? "Le plus urgent est en tête de fil, calé sur le calendrier judiciaire." : "Le cabinet surveille en continu les délais de recours."}
          </div>
        </div>

        {/* Compteur d'imminence (réel) : 2 critiques en tête + total 10 jours */}
        <div aria-label="Imminence des échéances" style={{ flex: "none", display: "flex", gap: isMobile ? 11 : 14, width: isMobile ? "100%" : "auto" }}>
          {criticalEvents.slice(0, 2).map(({ e, c }, i) => (
            <div key={e.id || i} style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : 104, borderRadius: 18, padding: "15px 16px", textAlign: "left", background: "rgba(168,50,74,.22)", border: "1px solid rgba(255,150,170,.4)", backdropFilter: "blur(8px)" }}>
              <div style={{ fontFamily: F.serifDisplay, fontWeight: 500, fontSize: isMobile ? 32 : 38, lineHeight: 1, letterSpacing: "-.02em", color: "#fff" }}>{c.code.replace("Demain · ", "").replace("Aujourd'hui", "J-0")}</div>
              <div style={{ fontSize: 11, color: "rgba(255,196,206,.92)", marginTop: 8, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{e._dossier?.client_name || e.summary}</div>
            </div>
          ))}
          {criticalEvents.length < 2 && (
            <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : 104, borderRadius: 18, padding: "15px 16px", textAlign: "left", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.14)", backdropFilter: "blur(8px)" }}>
              <div style={{ fontFamily: F.serifDisplay, fontWeight: 500, fontSize: isMobile ? 32 : 38, lineHeight: 1, letterSpacing: "-.02em", color: "#fff", fontVariantNumeric: "tabular-nums" }}>{tenDayCount}</div>
              <div style={{ fontSize: 11, color: "rgba(246,236,238,.7)", marginTop: 8, lineHeight: 1.35 }}>échéance{tenDayCount > 1 ? "s" : ""}<br />les 10 jours</div>
            </div>
          )}
        </div>
      </div>

      {/* ═ Anticipation : propositions de préparation (cron scan-audiences).
          L'avocat décide — « Préparer » ouvre la modale Confier pré-remplie,
          « Ignorer » rejette. Rien ne part sans son accord. ═ */}
      {(proposals?.length || 0) > 0 && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,.13)", padding: isMobile ? "15px 20px 18px" : "16px 34px 22px", display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(255,190,202,.92)", display: "flex", alignItems: "center", gap: 9 }}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: K.gildHi }} />
            Anticipation — le cabinet vous propose
          </div>
          {proposals.slice(0, 3).map(p => {
            const pl = p.action_payload || {};
            const evDate = pl.event_start_at ? new Date(pl.event_start_at) : null;
            const diff = evDate ? Math.ceil((evDate - now) / 86400000) : null;
            const when = diff === null ? "" : diff <= 0 ? "aujourd'hui" : diff === 1 ? "demain" : `dans ${diff} j`;
            const firstAction = (pl.suggested_actions || [])[0] || "Préparer cette échéance";
            return (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderRadius: 14, padding: "11px 14px", background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.13)" }}>
                <div style={{ flex: 1, minWidth: isMobile ? "100%" : 240 }}>
                  <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600, lineHeight: 1.35 }}>
                    {pl.category_label || "Audience"} « {pl.event_title || p.title} »{when ? ` ${when}` : ""} — préparer ?
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(246,236,238,.72)", marginTop: 3, lineHeight: 1.4 }}>
                    {firstAction}{pl.client_name ? ` · dossier ${pl.client_name}` : ""}{p.status === "approved" ? " · acceptée — à confier" : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flex: "none" }}>
                  <button
                    onClick={() => openProposalModal(p)}
                    aria-label={`Préparer : ${pl.event_title || p.title}`}
                    style={{ height: 34, padding: "0 14px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: F.sans, fontWeight: 700, fontSize: 12.5, color: K.bordeaux, background: "#fff", boxShadow: "0 6px 16px -6px rgba(0,0,0,.5)", display: "inline-flex", alignItems: "center", gap: 7 }}
                  >
                    <AgIc.arrow style={{ width: 13, height: 13 }} />
                    Préparer
                  </button>
                  <button
                    onClick={() => respondToProposal(p.id, "rejected")}
                    aria-label={`Ignorer la proposition : ${pl.event_title || p.title}`}
                    style={{ height: 34, padding: "0 12px", borderRadius: 9, cursor: "pointer", fontFamily: F.sans, fontWeight: 600, fontSize: 12.5, color: "rgba(246,236,238,.85)", background: "transparent", border: "1px solid rgba(255,255,255,.28)" }}
                  >
                    Ignorer
                  </button>
                </div>
              </div>
            );
          })}
          {proposals.length > 3 && (
            <div style={{ fontSize: 11.5, color: "rgba(246,236,238,.6)" }}>
              + {proposals.length - 3} autre{proposals.length - 3 > 1 ? "s" : ""} proposition{proposals.length - 3 > 1 ? "s" : ""} à venir
            </div>
          )}
        </div>
      )}
    </div>

    {/* ════════ Filtres : matière + horizon ════════ */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18, flexWrap: "wrap", padding: "4px 0 6px", marginBottom: 8 }}>
      <div role="group" aria-label="Filtrer par matière" style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: K.ink4, marginRight: 2 }}>Matière</span>
        {(() => {
          const allOn = catFilter === "all";
          return (
            <button onClick={() => setCatFilter("all")} aria-pressed={allOn} style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 13px", borderRadius: 99,
              fontFamily: F.sans, fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "box-shadow .2s, transform .2s",
              color: allOn ? "#fff" : K.ink2, border: `1px solid ${allOn ? "transparent" : K.hair}`,
              background: allOn ? `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})` : K.card,
              boxShadow: allOn ? `0 6px 14px -6px ${K.bordeauxGlow}` : K.lift1,
            }}>Toutes</button>
          );
        })()}
        {presentMatters.map(key => {
          const m = cabMatiere(key);
          const on = catFilter === key;
          return (
            <button key={key} onClick={() => setCatFilter(key)} aria-pressed={on} style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 13px", borderRadius: 99,
              fontFamily: F.sans, fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "box-shadow .2s, transform .2s",
              color: on ? "#fff" : K.ink2, border: `1px solid ${on ? "transparent" : K.hair}`,
              background: on ? `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})` : K.card,
              boxShadow: on ? `0 6px 14px -6px ${K.bordeauxGlow}` : K.lift1,
            }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: on ? "#fff" : m.color }} />
              {m.label}
            </button>
          );
        })}
      </div>
      <div role="group" aria-label="Horizon temporel" style={{ display: "flex", gap: 4, padding: 4, borderRadius: 99, background: K.card, border: `1px solid ${K.hair}`, boxShadow: K.lift1 }}>
        {HORIZONS.map(h => {
          const on = view === h.id;
          return (
            <button key={h.id} onClick={() => setView(h.id)} aria-pressed={on} style={{
              padding: "7px 14px", borderRadius: 99, fontFamily: F.sans, fontSize: 12.5, fontWeight: 600, cursor: "pointer", transition: "color .2s, background .2s",
              color: on ? "#fff" : K.ink3, background: on ? `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})` : "transparent",
              boxShadow: on ? `0 4px 12px -5px ${K.bordeauxGlow}` : "none", whiteSpace: "nowrap",
            }}>{h.label}</button>
          );
        })}
      </div>
    </div>

    {/* ════════ LA LIGNE DE VIE — fil chronologique vertical ════════ */}
    <section aria-label="Fil chronologique des échéances" style={{ padding: "14px 0 24px" }}>
      <div style={{ position: "relative" }}>
        {/* Colonne vertébrale */}
        <div aria-hidden="true" style={{ position: "absolute", left: isMobile ? 23 : 27, top: 0, bottom: 0, width: 2, background: `linear-gradient(180deg, ${K.hair3}, ${K.hair3} 92%, transparent)` }} />

        {/* Repère MAINTENANT */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16, padding: "6px 0 18px" }}>
          <div style={{ flex: "none", width: isMobile ? 46 : 54, display: "flex", justifyContent: "center" }}>
            <span aria-hidden="true" style={{ width: 16, height: 16, borderRadius: "50%", background: K.bordeaux, border: "3px solid var(--cab-card)", boxShadow: `0 0 0 5px ${K.bordeauxTint}`, animation: "cabBlink 2s var(--cab-ease-soft) infinite" }} />
          </div>
          <span style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: K.bordeaux, fontWeight: 500 }}>Maintenant · {nowTime}</span>
          <span aria-hidden="true" style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${K.bordeauxTint2}, transparent)` }} />
        </div>

        {loadingCal && events.length === 0 ? (
          <div role="status" aria-live="polite" style={{ marginLeft: isMobile ? 0 : 76, padding: "40px 18px", fontFamily: F.monoCab, fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: K.ink3, animation: "cabBlink 1.8s ease-in-out infinite" }}>
            Lecture du calendrier judiciaire…
          </div>
        ) : byDay.length === 0 ? (
          <div style={{ marginLeft: isMobile ? 0 : 76, marginTop: 6, borderRadius: 14, background: "color-mix(in srgb, var(--cab-card) 60%, transparent)", border: `1px solid ${K.hair}`, padding: "16px 18px", display: "flex", gap: 12, alignItems: "center", fontSize: 13.5, color: K.ink2, lineHeight: 1.45, maxWidth: 640 }}>
            <span aria-hidden="true" style={{ color: K.verified, display: "flex", width: 18, height: 18, flex: "none" }}><AgIc.shield style={{ width: 18, height: 18 }} /></span>
            <span><b style={{ color: K.ink, fontWeight: 600 }}>Horizon dégagé.</b> Aucune échéance {view === "mois" ? "ce mois" : view === "dix" ? "sous 10 jours" : "cette semaine"}{catFilter !== "all" ? " pour cette matière" : ""}. {isDemo ? "Connectez votre agenda dans Paramètres." : "Le cabinet surveille en continu le calendrier judiciaire."}</span>
          </div>
        ) : (
          <>
            {byDay.map(group => {
              const dl = agDayLabel(group.date, now);
              return (
                <div key={group.key}>
                  {/* Jalon de jour */}
                  <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16, padding: "26px 0 14px" }}>
                    <div aria-hidden="true" style={{ flex: "none", width: isMobile ? 46 : 54, display: "flex", justifyContent: "center" }}>
                      <span style={{ width: 14, height: 14, borderRadius: "50%", background: dl.isToday ? K.bordeaux : K.card, border: dl.isToday ? "2px solid var(--cab-card)" : `2px solid ${K.hair3}`, boxShadow: dl.isToday ? `0 0 0 4px ${K.bordeauxTint2}, ${K.lift1}` : K.lift1 }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: F.serifDisplay, fontWeight: 600, fontSize: isMobile ? 18 : 21, letterSpacing: "-.01em", color: K.ink }}>{dl.ddCap}</span>
                      {dl.rel && <span style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: ".14em", textTransform: "uppercase", color: dl.isToday ? K.bordeaux : K.ink3, fontWeight: dl.isToday ? 500 : 400 }}>{dl.rel}</span>}
                    </div>
                    <span aria-hidden="true" style={{ flex: 1, height: 1, background: K.hair2, marginLeft: 4 }} />
                  </div>

                  {/* Échéances du jour */}
                  {group.items.map((ev, idx) => {
                    const crit = agCriticity(ev, now);
                    const m = ev._m;
                    const dossier = ev._dossier;
                    const inferredMatiere = ev._matiereKey;
                    const referent = agentForMatiere(inferredMatiere); // collaborateur référent (dérivé matière)
                    const suggestedAgent = referent;
                    const PinIcon = AG_PIN_ICON[ev._cat] || AgIc.cal;
                    const isEvSelected = selectedEvent === (ev.id || `${group.key}-${idx}`);
                    const isDoneStatus = norm(ev.status) === "done" || norm(ev.status) === "completed" || norm(ev.status) === "cancelled";
                    const critBadge = crit.level === "urgent"
                      ? { fg: K.bordeaux, bg: K.bordeauxTint2, dotAnim: "cabBlink 1.6s infinite", suffix: ev._cat === "deadline" || ev._cat === "urgence" ? " · forclusion" : ev._cat === "audience" ? " · audience" : "" }
                      : crit.level === "soon"
                        ? { fg: K.gild, bg: K.gildTint, dotAnim: "none", suffix: "" }
                        : { fg: K.ink3, bg: K.hair, dotAnim: "none", suffix: "" };

                    return (
                      <article key={ev.id || `${group.key}-${idx}`} style={{ position: "relative", display: "flex", gap: isMobile ? 13 : 22, padding: "0 0 16px", alignItems: "stretch" }}>
                        {/* Stub : pastille + heure */}
                        <div style={{ flex: "none", width: isMobile ? 46 : 54, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 2, paddingTop: 18 }}>
                          <span style={{
                            width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 12, display: "grid", placeItems: "center",
                            color: crit.isCritical ? "#fff" : isDoneStatus ? "#fff" : K.ink3,
                            background: crit.isCritical ? `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})` : isDoneStatus ? (K.verified) : "var(--cab-card)",
                            border: crit.isCritical || isDoneStatus ? "none" : `1px solid ${K.hair2}`,
                            boxShadow: crit.isCritical ? `0 0 0 4px ${K.bordeauxTint}, 0 8px 18px -8px ${K.bordeauxGlow}` : K.lift1,
                          }}><PinIcon style={{ width: 18, height: 18 }} /></span>
                          <span style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: ".02em", color: crit.isCritical ? K.bordeaux : K.ink4, fontWeight: crit.isCritical ? 500 : 400, marginTop: 8, whiteSpace: "nowrap" }}>
                            {ev.date?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>

                        {/* Carte d'échéance */}
                        <div
                          onClick={() => setSelectedEvent(isEvSelected ? null : (ev.id || `${group.key}-${idx}`))}
                          style={{
                            flex: 1, minWidth: 0, position: "relative", borderRadius: 18, overflow: "hidden", cursor: "pointer",
                            background: crit.isCritical ? `radial-gradient(130% 110% at 0% 0%, ${K.bordeauxTint}, transparent 62%), var(--cab-card)` : isDoneStatus ? K.cardSoft : K.card,
                            border: `1px solid ${crit.isCritical ? K.bordeauxTint2 : K.hair}`,
                            boxShadow: crit.isCritical ? `0 18px 42px -22px ${K.bordeauxGlow}, ${K.lift2}` : isDoneStatus ? K.lift1 : K.lift2,
                            transition: "box-shadow .3s var(--cab-ease), transform .3s var(--cab-ease)",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.boxShadow = K.lift3; e.currentTarget.style.transform = "translateY(-2px)"; }}
                          onMouseLeave={e => { e.currentTarget.style.boxShadow = crit.isCritical ? `0 18px 42px -22px ${K.bordeauxGlow}, ${K.lift2}` : isDoneStatus ? K.lift1 : K.lift2; e.currentTarget.style.transform = "translateY(0)"; }}
                        >
                          {/* Filet de matière à gauche */}
                          <span aria-hidden="true" style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: isDoneStatus ? (K.verified) : m.color }} />
                          <div style={{ padding: "17px 19px 16px", paddingLeft: 23 }}>
                            {/* Haut : matière + criticité */}
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
                              <span style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 10px", borderRadius: 99, color: m.color, background: m.soft }}>
                                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                                {m.label}{ev.location ? ` · ${ev.location}` : ev._catLabel && ev._catLabel !== "Autre" ? ` · ${ev._catLabel}` : ""}
                              </span>
                              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 99, whiteSpace: "nowrap", color: critBadge.fg, background: critBadge.bg }}>
                                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", animation: critBadge.dotAnim }} />
                                {crit.code}{critBadge.suffix}
                              </span>
                            </div>

                            {/* Titre + nature */}
                            <div style={{ fontFamily: F.serifDisplay, fontWeight: 600, fontSize: isMobile ? 17 : 19, lineHeight: 1.18, letterSpacing: "-.01em", color: isDoneStatus ? K.ink3 : K.ink, marginTop: 13 }}>{ev.summary || "Échéance"}</div>
                            <div style={{ fontSize: 12.5, color: K.ink2, marginTop: 5, display: "flex", alignItems: "center", gap: 8, lineHeight: 1.45, flexWrap: "wrap" }}>
                              <span aria-hidden="true" style={{ color: K.ink3, display: "flex", width: 15, height: 15, flex: "none" }}><AgIc.clock style={{ width: 15, height: 15 }} /></span>
                              <b style={{ color: K.ink, fontWeight: 600 }}>{ev.date?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</b>
                              {ev.endDate && <span style={{ color: K.ink3 }}>→ {ev.endDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
                              {dossier && <><span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: "50%", background: K.ink4 }} />{dossier.client_name}{dossier.dossier_ref ? <span style={{ fontFamily: F.monoCab, color: K.ink3 }}>{` · ${dossier.dossier_ref}`}</span> : ""}</>}
                            </div>

                            {/* Bandeau d'ANTICIPATION — le référent du dossier.
                                NOTE HONNÊTETÉ : calendar_events ne porte ni collaborateur
                                ni état de préparation. On dérive le RÉFÉRENT depuis la
                                matière (agentForMatiere — même logique que la suggestion
                                d'agent existante) ; on n'invente PAS un « prêt / 88 % ». */}
                            <div style={{
                              marginTop: 14, borderRadius: 13, padding: "12px 14px", display: "flex", alignItems: "center", gap: 13, lineHeight: 1.4,
                              background: crit.isCritical ? K.bordeauxTint : "color-mix(in srgb, var(--cab-card) 60%, transparent)",
                              border: `1px solid ${crit.isCritical ? K.bordeauxTint2 : K.hair2}`,
                            }}>
                              {referent ? (
                                <span style={{ position: "relative", width: 38, height: 38, flex: "none" }}>
                                  <img src={referent.photoUrl} alt="" loading="lazy" style={{ width: "100%", height: "100%", borderRadius: 11, objectFit: "cover", objectPosition: "center top", border: `1px solid ${K.hair2}` }} />
                                  <span aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: -3, height: 3, borderRadius: 99, background: m.color }} />
                                </span>
                              ) : (
                                <span style={{ width: 30, height: 30, borderRadius: 9, flex: "none", display: "grid", placeItems: "center", background: K.verified, color: "#fff" }}>
                                  <AgIc.shield style={{ width: 16, height: 16 }} />
                                </span>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, color: K.ink2, fontWeight: 500 }}>
                                  {referent ? (
                                    <>Échéance suivie par <b style={{ color: K.ink, fontWeight: 600 }}>{referent.name}</b>{crit.isCritical ? <> — à préparer <b style={{ color: K.bordeaux, fontWeight: 600 }}>en priorité</b>.</> : "."}</>
                                  ) : (
                                    <>Sur le radar du cabinet — <b style={{ color: K.ink, fontWeight: 600 }}>aucun délai ne passe</b>.</>
                                  )}
                                </div>
                                <div style={{ fontSize: 11.5, color: K.ink3, marginTop: 3, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                  {referent ? <>{referent.name} · {referent.title}</> : "Veille des délais · Sophie"}
                                </div>
                              </div>
                            </div>

                            {/* Détail déplié + actions (décisions) */}
                            {isEvSelected && (
                              <div style={{ marginTop: 14, animation: "cabRise .22s var(--cab-ease)" }}>
                                {(ev.location || ev.description || (ev.endDate)) && (
                                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: ev.description ? 12 : 4 }}>
                                    <div>
                                      <div style={{ fontFamily: F.monoCab, fontSize: 9.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: K.ink4, marginBottom: 4 }}>Début</div>
                                      <div style={{ fontFamily: F.sans, fontSize: 13, color: K.ink, fontWeight: 500 }}>{ev.date?.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} · <span style={{ fontFamily: F.monoCab }}>{ev.date?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span></div>
                                    </div>
                                    <div>
                                      <div style={{ fontFamily: F.monoCab, fontSize: 9.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: K.ink4, marginBottom: 4 }}>Fin</div>
                                      <div style={{ fontFamily: F.sans, fontSize: 13, color: K.ink, fontWeight: 500 }}><span style={{ fontFamily: F.monoCab }}>{ev.endDate?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) || "—"}</span></div>
                                    </div>
                                  </div>
                                )}
                                {ev.description && <div style={{ fontFamily: F.sans, fontSize: 13, color: K.ink2, lineHeight: 1.55, maxHeight: 120, overflowY: "auto", marginBottom: 4, letterSpacing: "-.005em" }}>{ev.description}</div>}
                              </div>
                            )}

                            {/* Rangée d'actions */}
                            <div style={{ display: "flex", gap: 9, marginTop: 14, flexWrap: "wrap" }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setAssignModal({ event: ev, dossier, agent: suggestedAgent || null, instructions: "" }); }}
                                style={{ height: 38, padding: "0 16px", borderRadius: 10, fontFamily: F.sans, fontWeight: 600, fontSize: 13, color: "#fff", background: `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})`, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: `0 8px 18px -8px ${K.bordeauxGlow}`, transition: "transform .2s, filter .2s" }}
                                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.filter = "brightness(1.04)"; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.filter = "none"; }}
                              >
                                <AgIc.arrow style={{ width: 15, height: 15 }} />
                                Confier{referent ? ` à ${referent.name}` : " à un agent"}
                              </button>
                              {!dossier && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const tokens = (ev.summary || "").split(/[\s\-/,;:()[\]._|]+/).filter(Boolean);
                                    const upper = tokens.filter(t => t.length >= 3 && /^[A-ZÉÈÀÇ]+$/.test(t) && !["GAV", "MP", "JLD", "TGI", "TC", "JAF", "CRPC", "TPE", "CA", "RE", "RG", "MME", "MR", "JI", "JEX"].includes(t));
                                    const firstUpper = upper[0];
                                    const firstIdx = firstUpper ? tokens.indexOf(firstUpper) : -1;
                                    const firstName = firstIdx >= 0 && tokens[firstIdx + 1] && /^[A-Z][a-zéèàç]/.test(tokens[firstIdx + 1]) ? tokens[firstIdx + 1] : "";
                                    const candidate = [firstUpper, firstName].filter(Boolean).join(" ") || "";
                                    setCreateDossierModal({ source_type: "event", source_id: ev.id, source_title: ev.summary, client_name: candidate, matiere: inferredMatiere || "a_definir" });
                                  }}
                                  style={{ height: 38, padding: "0 15px", borderRadius: 10, fontFamily: F.sans, fontWeight: 600, fontSize: 13, color: K.ink, background: "var(--cab-card)", border: `1px solid ${K.hair2}`, display: "inline-flex", alignItems: "center", gap: 7, boxShadow: K.lift1, transition: "box-shadow .2s, transform .2s" }}
                                  onMouseEnter={e => { e.currentTarget.style.boxShadow = K.lift2; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = K.lift1; e.currentTarget.style.transform = "translateY(0)"; }}
                                >
                                  <AgIc.folder style={{ width: 14, height: 14 }} />
                                  Créer un dossier
                                </button>
                              )}
                              {referent && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onAssignToAgent?.({ agent: referent, event: ev, dossier, instructions: "" }); }}
                                  style={{ height: 38, padding: "0 13px 0 9px", borderRadius: 10, fontFamily: F.sans, fontWeight: 600, fontSize: 13, color: K.ink, background: "var(--cab-card)", border: `1px solid ${K.hair2}`, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: K.lift1, transition: "box-shadow .2s, transform .2s" }}
                                  onMouseEnter={e => { e.currentTarget.style.boxShadow = K.lift2; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = K.lift1; e.currentTarget.style.transform = "translateY(0)"; }}
                                >
                                  <img src={referent.photoUrl} alt="" loading="lazy" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", objectPosition: "center top" }} />
                                  Parler à {referent.name}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              );
            })}

            {/* Note de fin de fil : horizon dégagé */}
            <div style={{ marginLeft: isMobile ? 0 : 76, marginTop: 6, borderRadius: 14, background: "color-mix(in srgb, var(--cab-card) 60%, transparent)", border: `1px solid ${K.hair}`, padding: "14px 16px", display: "flex", gap: 12, alignItems: "center", fontSize: 13, color: K.ink2, lineHeight: 1.45, maxWidth: 640 }}>
              <span aria-hidden="true" style={{ color: K.verified, display: "flex", width: 18, height: 18, flex: "none" }}><AgIc.shield style={{ width: 18, height: 18 }} /></span>
              <span><b style={{ color: K.ink, fontWeight: 600 }}>Au-delà, rien d'urgent.</b> Le cabinet surveille en continu le calendrier judiciaire et les délais de recours. Vous serez prévenu·e dès qu'une nouvelle échéance se profile.</span>
            </div>
          </>
        )}
      </div>
    </section>
      </div>
    </div>

    {/* Modal — Créer un dossier depuis l'event (socle Le Cabinet) */}
    {createDossierModal && (
      <div style={{
        position: "fixed", inset: 0, background: "var(--overlay)", backdropFilter: "blur(3px)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }} onClick={() => !creatingDossier && setCreateDossierModal(null)}>
        <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Créer un dossier" style={{
          background: K.card, width: "100%", maxWidth: 480, borderRadius: K.rLg,
          border: `1px solid ${K.hair}`, boxShadow: K.lift3, fontFamily: F.sans,
        }}>
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${K.hair}` }}>
            <h3 style={{ fontFamily: F.serifDisplay, fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", color: K.ink, margin: 0 }}>Créer un dossier</h3>
            <div style={{ fontSize: 12.5, color: K.ink3, marginTop: 4 }}>Depuis : {createDossierModal.source_title}</div>
          </div>
          <div style={{ padding: 22 }}>
            <label style={{ fontFamily: F.monoCab, fontSize: 10.5, fontWeight: 600, color: K.ink3, textTransform: "uppercase", letterSpacing: ".06em" }}>Nom du client</label>
            <input value={createDossierModal.client_name}
              onChange={e => setCreateDossierModal(m => ({ ...m, client_name: e.target.value }))}
              placeholder="ex: DUPONT Jean"
              style={{ width: "100%", boxSizing: "border-box", padding: "11px 13px", marginTop: 7, borderRadius: K.rSm, border: `1px solid ${K.hair2}`, fontSize: 14, fontFamily: F.sans, color: K.ink, background: K.cardSoft, outline: "none", marginBottom: 16 }} />
            <label style={{ fontFamily: F.monoCab, fontSize: 10.5, fontWeight: 600, color: K.ink3, textTransform: "uppercase", letterSpacing: ".06em" }}>Matière</label>
            <select value={createDossierModal.matiere}
              onChange={e => setCreateDossierModal(m => ({ ...m, matiere: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "11px 13px", marginTop: 7, borderRadius: K.rSm, border: `1px solid ${K.hair2}`, fontSize: 14, fontFamily: F.sans, color: K.ink, background: K.cardSoft, marginBottom: 6 }}>
              <option value="a_definir">À définir</option>
              <option value="penal">Pénal</option>
              <option value="civil">Civil</option>
              <option value="famille">Famille</option>
              <option value="corporel">Corporel</option>
              <option value="successions">Successions</option>
              <option value="travail">Travail</option>
              <option value="affaires">Affaires / Sociétés</option>
              <option value="commercial">Commercial</option>
              <option value="immobilier">Immobilier</option>
              <option value="administratif">Administratif</option>
              <option value="fiscal">Fiscal</option>
              <option value="procedures-collectives">Procédures collectives</option>
              <option value="consommation">Consommation</option>
              <option value="pi">Propriété intellectuelle</option>
              <option value="rgpd">RGPD</option>
              <option value="environnement">Environnement</option>
            </select>
          </div>
          <div style={{ padding: "16px 22px", borderTop: `1px solid ${K.hair}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setCreateDossierModal(null)} disabled={creatingDossier}
              style={{ padding: "10px 18px", borderRadius: K.rSm, fontSize: 13.5, fontWeight: 600, cursor: "pointer", border: `1px solid ${K.hair2}`, background: "var(--cab-card)", color: K.ink2, fontFamily: F.sans, boxShadow: K.lift1 }}>
              Annuler
            </button>
            <button
              disabled={creatingDossier || !createDossierModal.client_name?.trim()}
              onClick={async () => {
                setCreatingDossier(true);
                try {
                  // Endpoint sécurisé (IDOR) : le serveur dérive le lawyer du JWT
                  // et exige Authorization. On lit la session courante avant l'appel.
                  const { data: { session } } = await supabase.auth.getSession();
                  if (!session?.access_token) { toast.error('Session expirée. Veuillez vous reconnecter.'); setCreatingDossier(false); return; }
                  const resp = await fetch('/api/dossier/create-from-source', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                      lawyer_id: lawyer?.id,
                      source_type: createDossierModal.source_type,
                      source_id: createDossierModal.source_id,
                      client_name: createDossierModal.client_name,
                      matiere: createDossierModal.matiere,
                    }),
                  });
                  if (resp.status === 401) { toast.error('Session expirée. Veuillez vous reconnecter.'); setCreatingDossier(false); return; }
                  const data = await resp.json();
                  if (resp.status === 403 && data.code === 'SUB_INACTIVE') { toast.error('Votre abonnement est inactif. Régularisez-le dans les Paramètres pour créer un dossier.'); setCreatingDossier(false); return; }
                  if (!data.success) { toast.error('Échec de la création du dossier : ' + (data.error || 'erreur inconnue')); setCreatingDossier(false); return; }
                  toast.success(`Dossier « ${data.dossier.client_name} » créé (${data.dossier.dossier_ref}).`, { duration: 6000 });
                  setCreateDossierModal(null);
                  // Met à jour l'event localement (dossierId lié)
                  setCalEvents(prev => (prev || []).map(e => e.id === createDossierModal.source_id ? { ...e, dossierId: data.dossier.id } : e));
                } catch (e) { toast.error('Erreur réseau : ' + e.message); }
                setCreatingDossier(false);
              }}
              style={{ padding: "10px 22px", borderRadius: K.rSm, fontSize: 13.5, fontWeight: 600, cursor: creatingDossier ? "wait" : "pointer", border: "1px solid transparent", background: `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})`, color: "#fff", fontFamily: F.sans, boxShadow: `0 8px 18px -8px ${K.bordeauxGlow}`, opacity: creatingDossier ? 0.7 : 1 }}>
              {creatingDossier ? "Création…" : "Créer le dossier"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal — Confier une tâche à un agent (socle Le Cabinet) */}
    {assignModal && (
      <div style={{
        position: "fixed", inset: 0, background: "var(--overlay)", backdropFilter: "blur(3px)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }} onClick={() => setAssignModal(null)}>
        <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Confier à un agent" style={{
          background: K.card, width: "100%", maxWidth: 560, maxHeight: "85vh",
          borderRadius: K.rLg, display: "flex", flexDirection: "column",
          border: `1px solid ${K.hair}`, boxShadow: K.lift3, fontFamily: F.sans,
        }}>
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${K.hair}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ fontFamily: F.serifDisplay, fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", color: K.ink, margin: 0 }}>Confier à un collaborateur</h3>
              <div style={{ fontSize: 12.5, color: K.ink3, marginTop: 4 }}>{assignModal.event.summary}</div>
            </div>
            <button onClick={() => setAssignModal(null)} aria-label="Fermer" style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer", color: K.ink4, padding: 4 }}>✕</button>
          </div>
          <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>
            {/* Contexte */}
            <div style={{ padding: "12px 14px", borderRadius: K.rSm, background: K.cardSoft, border: `1px solid ${K.hair2}`, marginBottom: 18, fontSize: 13, color: K.ink2, lineHeight: 1.6 }}>
              <div><span style={{ fontFamily: F.monoCab, fontVariantNumeric: "tabular-nums" }}>{assignModal.event.date?.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span> · <span style={{ fontFamily: F.monoCab, fontVariantNumeric: "tabular-nums" }}>{assignModal.event.date?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span></div>
              {assignModal.event.location && <div>{assignModal.event.location}</div>}
              {assignModal.dossier && <div style={{ color: K.bordeaux, fontWeight: 600 }}>{assignModal.dossier.client_name}{assignModal.dossier.matiere ? ` · ${cabMatiere(assignModal.dossier.matiere).label}` : ''}</div>}
            </div>

            {/* Sélection agent */}
            <label style={{ fontFamily: F.monoCab, fontSize: 10.5, fontWeight: 600, color: K.ink3, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Collaborateur{assignModal.dossier?.matiere ? ' · référent selon la matière' : ' · aucune matière détectée'}
            </label>
            <select
              value={assignModal.agent?.id || ''}
              onChange={e => {
                const a = AGENTS.find(x => x.id === e.target.value);
                setAssignModal(m => ({ ...m, agent: a || null }));
              }}
              style={{
                width: "100%", boxSizing: "border-box", padding: "11px 13px", marginTop: 7, borderRadius: K.rSm, border: `1px solid ${K.hair2}`,
                fontSize: 14, fontFamily: F.sans, color: K.ink, background: K.cardSoft, outline: "none", marginBottom: 18,
              }}
            >
              <option value="">— Choisir un agent —</option>
              {Object.entries(AGENT_CATEGORIES).map(([catId, cat]) => {
                const group = AGENTS.filter(a => a.category === catId);
                if (group.length === 0) return null;
                return (
                  <optgroup key={catId} label={cat.label}>
                    {group.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} — {a.title}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>

            {/* Bio agent sélectionné — filet coloré = palette catégorielle des agents (data) */}
            {assignModal.agent && (
              <div style={{ padding: "12px 14px", borderRadius: K.rSm, background: K.cardSoft, border: `1px solid ${K.hair2}`, borderLeft: `3px solid ${AGENT_CATEGORIES[assignModal.agent.category]?.color || K.bordeaux}`, fontSize: 13, color: K.ink2, lineHeight: 1.6, marginBottom: 18, display: "flex", gap: 12, alignItems: "flex-start" }}>
                {assignModal.agent.photoUrl && <img src={assignModal.agent.photoUrl} alt="" loading="lazy" style={{ width: 36, height: 36, borderRadius: 10, objectFit: "cover", objectPosition: "center top", flex: "none", border: `1px solid ${K.hair2}` }} />}
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: K.ink, fontWeight: 600 }}>{assignModal.agent.name}</strong> · {assignModal.agent.title}
                  <div style={{ marginTop: 5, color: K.ink3 }}>{assignModal.agent.bio}</div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <label style={{ fontFamily: F.monoCab, fontSize: 10.5, fontWeight: 600, color: K.ink3, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Votre demande
            </label>
            <textarea
              value={assignModal.instructions}
              onChange={e => setAssignModal(m => ({ ...m, instructions: e.target.value }))}
              placeholder="Ex : Prépare-moi un mémo de défense avec les pièces au dossier · Rédige un projet de conclusions · Vérifie les nullités de garde-à-vue…"
              rows={5}
              style={{
                width: "100%", boxSizing: "border-box", padding: 13, marginTop: 7, borderRadius: K.rSm, border: `1px solid ${K.hair2}`,
                fontSize: 14, fontFamily: F.sans, color: K.ink, lineHeight: 1.55, outline: "none",
                resize: "vertical", minHeight: 104, background: K.cardSoft,
              }}
            />
          </div>
          <div style={{ padding: "16px 22px", borderTop: `1px solid ${K.hair}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setAssignModal(null)}
              style={{ padding: "10px 18px", borderRadius: K.rSm, fontSize: 13.5, fontWeight: 600, cursor: "pointer", border: `1px solid ${K.hair2}`, background: "var(--cab-card)", color: K.ink2, fontFamily: F.sans, boxShadow: K.lift1 }}>
              Annuler
            </button>
            <button
              disabled={!assignModal.agent}
              onClick={() => {
                if (!assignModal.agent) return;
                onAssignToAgent?.({
                  agent: assignModal.agent,
                  event: assignModal.event,
                  dossier: assignModal.dossier,
                  instructions: assignModal.instructions,
                });
                // Proposition d'anticipation : le travail part dans le chat
                // → la proposition est exécutée (cf. cron scan-audiences).
                if (assignModal.proposalId) respondToProposal(assignModal.proposalId, 'executed');
                setAssignModal(null);
              }}
              style={{
                padding: "10px 22px", borderRadius: K.rSm, fontSize: 13.5, fontWeight: 600,
                cursor: assignModal.agent ? "pointer" : "not-allowed",
                border: "1px solid transparent",
                background: assignModal.agent ? `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})` : K.hair,
                color: assignModal.agent ? "#fff" : K.ink4,
                boxShadow: assignModal.agent ? `0 8px 18px -8px ${K.bordeauxGlow}` : "none",
                fontFamily: F.sans,
              }}>
              Confier
            </button>
          </div>
        </div>
      </div>
    )}
  </>;
}


// ─── Main App ───
export default function App() {
  const { lawyer: authLawyer, user, signOut } = useAuth();
  const toast = useToast();
  // Navigation state synced with browser history for back button support
  // "chat" requires an active agent, so on refresh fall back to "home"
  const REFRESH_SAFE_PAGES = new Set(["home", "team", "dossiers", "jurisprudence", "agenda", "emails", "settings", "support", "acte"]);
  const [page, setPageRaw] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return (hash && REFRESH_SAFE_PAGES.has(hash)) ? hash : "home";
  });
  const setPage = (newPage) => {
    setPageRaw(newPage);
    window.history.pushState({ page: newPage }, '', `#${newPage}`);
  };
  useEffect(() => {
    const handlePopState = (e) => {
      const p = e.state?.page || window.location.hash.replace('#', '') || 'home';
      setPageRaw(p);
    };
    window.addEventListener('popstate', handlePopState);
    // Set initial history state
    if (!window.location.hash) window.history.replaceState({ page: 'home' }, '', '#home');
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [lawyer, setLawyer] = useState(null);
  const [stats, setStats] = useState({ mMsg: 0, convos: 0 });
  const [msgs, setMsgs] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // (Chrome E : plus de sidebar repliable — la nav vit dans la top-nav horizontale.)

  // Dark mode — drives <html data-theme="dark"> via CSS variables. The
  // legacy `T` proxy from dashboardHelpers continues to read from LIGHT for
  // back-compat (most components import T statically).
  const [darkMode, setDarkMode] = useState(() => initDarkModeFromStorage());
  syncDarkMode(false); // T proxy reads light tokens — dark is handled at CSS level
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      setHtmlTheme(next);
      return next;
    });
  };

  // Initialize auth headers when session changes
  useEffect(() => {
    async function updateHeaders() {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      syncSbHeaders({ apikey: apiKey, Authorization: `Bearer ${token || apiKey}` });
    }
    updateHeaders();
  }, [user]);

  const LID = authLawyer?.id || '';

  const load = useCallback(async () => {
    if (!LID) { setLoading(false); return; }
    setLoading(true);
    try {
      // Une seule requête agrégatrice — voir api/dashboard/bootstrap.js.
      const data = await bootstrapDashboard();
      setLawyer(data.lawyer || authLawyer || null);
      setMsgs(data.messages || []);
      setDossiers(data.dossiers || []);
      setFiles(data.files || []);
      setStats(data.stats || {});
    } catch (err) {
      // Fallback : cascade REST si l'endpoint d'agrégation a un souci.
      console.warn('[DashboardV5] bootstrap endpoint failed, fallback to cascade:', err.message);
      const now = new Date();
      const ms = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const weekStart = new Date(now); const day = (weekStart.getDay() + 6) % 7; weekStart.setDate(weekStart.getDate() - day); weekStart.setHours(0,0,0,0);
      const nextWeek = new Date(now.getTime() + 7 * 86400000).toISOString();
      const nowIso = now.toISOString();
      const [l, m, cv, d, f, mCount, upcomingEvents, docsWeek] = await Promise.all([
        sbGet(`/lawyers?id=eq.${LID}&limit=1`),
        sbGet(`/messages?lawyer_id=eq.${LID}&order=created_at.desc&limit=50&select=id,role,content,created_at,tokens_input,tokens_output`),
        sbGet(`/conversations?lawyer_id=eq.${LID}&select=id`),
        sbGet(`/client_dossiers?lawyer_id=eq.${LID}&order=created_at.desc`),
        sbGet(`/client_files?lawyer_id=eq.${LID}&order=created_at.desc`),
        sbCount(`/messages?lawyer_id=eq.${LID}&role=eq.user&created_at=gte.${ms}`),
        sbGet(`/calendar_events?lawyer_id=eq.${LID}&start_at=gte.${nowIso}&start_at=lte.${nextWeek}&order=start_at.asc&select=start_at,title&limit=50`),
        sbCount(`/generated_documents?lawyer_id=eq.${LID}&created_at=gte.${weekStart.toISOString()}`),
      ]);
      setLawyer(l[0] || authLawyer || null); setMsgs(m); setDossiers(d); setFiles(f);
      const next = upcomingEvents?.[0]?.start_at ? new Date(upcomingEvents[0].start_at).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
      setStats({
        mMsg: mCount,
        convos: cv.length,
        upcomingEvents: upcomingEvents?.length || 0,
        nextEventDate: next,
        docsGenerated: docsWeek,
      });
    } finally {
      setLoading(false);
    }
  }, [LID]);
  useEffect(() => { load(); }, [load]);

  // Unread email count for the sidebar nav badge.
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  useEffect(() => {
    if (!LID) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`/api/auth/refresh-token?lawyer_id=${LID}&type=unread_count`);
        const d = await r.json();
        if (!cancelled && d.success) setUnreadEmailCount(d.unread_count || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [LID]);

  // Microsoft OAuth callback handler (server-side callback returns ?ms_auth=success/error)
  const [msStatus, setMsStatus] = useState(null); // null | "processing" | "success" | "error"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const msAuth = params.get("ms_auth");
    const msEmail = params.get("email");
    if (msAuth === "success") {
      setMsStatus("success");
      setPage("settings");
      window.history.replaceState({}, "", window.location.pathname);
      // Reload integrations cache to show updated status
      load();
      setTimeout(() => setMsStatus(null), 5000);
    } else if (msAuth === "error") {
      setMsStatus("error");
      setPage("settings");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setMsStatus(null), 5000);
    }
  }, [LID]);

  // Dropbox OAuth callback handler
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (code && state === "dbx_oauth" && LID && DBX_APP_KEY) {
      setMsStatus("processing"); // Reuse msStatus for simplicity
      setPage("settings");
      window.history.replaceState({}, "", window.location.pathname);
      (async () => {
        const result = await handleDropboxCallback(code);
        if (result.success) {
          const lid = LID.substring(0, 8);
          const intRes = await sbGet(`/prompts?id=eq.INTEGRATIONS-${lid}&active=eq.true&select=content&limit=1`);
          let integrations = {};
          try { integrations = JSON.parse(intRes?.[0]?.content || "{}"); } catch(e) {}
          integrations["dropbox"] = { connected: true, email: result.email, provider: "dropbox", connected_at: new Date().toISOString(), access_token: result.tokens.access_token, refresh_token: result.tokens.refresh_token };
          await sbUpsert("prompts", { id: `INTEGRATIONS-${lid}`, agent: "system", matiere: "integrations", model: "multi", content: JSON.stringify(integrations), version: 1, active: true });
          setMsStatus("success");
          setTimeout(() => setMsStatus(null), 5000);
        } else {
          console.error("Dropbox OAuth error:", result.error);
          setMsStatus("error");
          setTimeout(() => setMsStatus(null), 5000);
        }
      })();
    }
  }, [LID]);

  // Google OAuth callback handler (serverless function redirects here with ?google_auth=success/error)
  const [googleAuthStatus, setGoogleAuthStatus] = useState(null); // null | "success" | "error"
  const [googleAuthEmail, setGoogleAuthEmail] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gAuth = params.get("google_auth");
    if (gAuth) {
      window.history.replaceState({}, "", window.location.pathname);
      if (gAuth === "success") {
        setGoogleAuthStatus("success");
        setGoogleAuthEmail(params.get("email") || "");
        setPage("settings");
        setTimeout(() => setGoogleAuthStatus(null), 6000);
      } else {
        setGoogleAuthStatus("error");
        setPage("settings");
        setTimeout(() => setGoogleAuthStatus(null), 8000);
      }
    }
  }, []);

  // Stripe Checkout return handler — create-checkout pose success_url
  // ?stripe_success=true&plan=… et cancel_url ?stripe_cancel=true. Le webhook
  // met l'abonnement à jour de façon ASYNCHRONE : on confirme par un toast, on
  // nettoie l'URL, on route vers Paramètres (où s'affiche l'abonnement) et on
  // recharge le profil (immédiat + un rappel court, le temps que le webhook pose
  // subscription_status='active'). Sans ça, l'utilisateur revenait de Stripe sur
  // un tableau de bord muet, profil périmé. La garde !LID évite que la première
  // passe (auth pas encore résolue) ne nettoie l'URL avant le refetch utile.
  useEffect(() => {
    if (!LID) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_success") === "true") {
      const planLabel = PLANS_CATALOG.find((p) => p.key === params.get("plan"))?.label;
      window.history.replaceState({}, "", window.location.pathname);
      toast.success(
        planLabel
          ? `Paiement confirmé — formule ${planLabel} en cours d'activation. Merci !`
          : "Paiement confirmé — votre abonnement est en cours d'activation.",
        { duration: 8000 }
      );
      setPage("settings");
      load(); // immédiat
      const t = setTimeout(() => load(), 4000); // rappel : laisse le webhook poser le statut
      return () => clearTimeout(t);
    }
    if (params.get("stripe_cancel") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      toast.info("Paiement annulé — vous pourrez souscrire plus tard depuis Paramètres → Abonnement.", { duration: 7000 });
    }
  }, [LID]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pendingSkill, setPendingSkill] = useState(null); // skill slug from TeamPage → ChatPage
  const [pendingContext, setPendingContext] = useState(null); // email context for Sophie
  const [acteContext, setActeContext] = useState(null); // selected dossier for the editor
  // Deep-link : dossier id à ouvrir directement (le fil). Plus personne ne
  // l'alimente depuis la purge de « Vos affaires » (Accueil refondu) — le
  // mécanisme reste car le bloc « À arbitrer » de la galerie refondue s'en
  // resservira pour ancrer le fil sur une décision.
  const [dossierToOpen, setDossierToOpen] = useState(null);
  const isCompact = useIsCompact();
  const isMobile = useIsMobile();

  // Nav « E » : liens principaux dans la top-nav horizontale + entrées
  // secondaires dans le menu compte (avatar). Libellés E (« Accueil »).
  // Les counts dynamiques (dossiers, agenda, emails non-lus) sont préservés.
  // Badge « Dossiers » : affaires VIVANTES uniquement — la galerie masque les
  // clôturées/archivées, le badge ne doit pas annoncer un autre compte.
  const liveDossiersCount = (dossiers || []).filter(d => {
    const s = d.status || "active";
    return s !== "cloture" && s !== "archive";
  }).length;
  const primaryNav = [
    { id: "home",          label: "Accueil",        count: null },
    { id: "dossiers",      label: "Dossiers",       count: liveDossiersCount || null },
    { id: "jurisprudence", label: "Jurisprudence",  count: null },
    { id: "agenda",        label: "Agenda",         count: stats?.upcomingEvents || null },
    { id: "emails",        label: "Emails",         count: unreadEmailCount || null },
    { id: "team",          label: "Collaborateurs", count: null },
  ];
  const secondaryNav = [
    { id: "support", label: "Mes demandes", count: null },
    { id: "settings", label: "Paramètres", count: null },
  ];

  const handleNav = (id) => { setPage(id); };
  const initials = (((lawyer?.first_name || '')[0] || '') + ((lawyer?.last_name || '')[0] || '')).toUpperCase() || 'AV';
  const planLabel = (lawyer?.plan || '').charAt(0).toUpperCase() + (lawyer?.plan || '').slice(1) || '—';
  const planLimit = PLANS[lawyer?.plan] || 0;
  const planPct = planLimit > 0 ? Math.min(100, Math.round((stats.mMsg / planLimit) * 100)) : 0;
  const monthLabel = new Date().toLocaleDateString('fr-FR', { month: 'long' });

  // Chrome E — barre de navigation horizontale (TopNav), rendue plus bas.
  // Le quota / la déconnexion / Paramètres vivent dans le menu compte (avatar).
  const handleSignOut = () => { signOut(); window.location.href = '/login'; };
  const topNav = (
    <TopNav
      page={page}
      primaryNav={primaryNav}
      secondaryNav={secondaryNav}
      handleNav={handleNav}
      onNewConversation={() => { setPendingSkill(null); setPendingContext(null); setPage('chat'); }}
      onOpenSearch={() => { /* ⌘K — recherche globale (à brancher) */ }}
      darkMode={darkMode}
      onToggleDark={toggleDarkMode}
      unreadEmailCount={unreadEmailCount}
      lawyer={lawyer}
      initials={initials}
      planLabel={planLabel}
      planLimit={planLimit}
      planPct={planPct}
      monthLabel={monthLabel}
      monthlyUsage={stats.mMsg}
      onSignOut={handleSignOut}
      isCompact={isCompact}
    />
  );

  // Acte editor — full-screen 3-column layout, bypasses dashboard chrome.
  // Lazy-loadé : Suspense pleine hauteur (l'éditeur occupe tout l'écran).
  if (page === 'acte') {
    return (
      <Suspense fallback={<div style={{ height: "100vh", background: C.bgPage }}><PageFallback full /></div>}>
        <ActeEditorPage
          dossier={acteContext}
          onBack={() => { setActeContext(null); setPage(acteContext ? 'dossiers' : 'home'); }}
        />
      </Suspense>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: F.sans, background: C.bgPage }}>
      {/* Chrome E — barre de navigation horizontale, pleine largeur, sticky.
          Remplace la sidebar encre. Inclut son propre menu nav mobile (hamburger). */}
      {topNav}

      {/* Bottom Navigation (mobile only) — handoff Module D Mobile, 5 onglets */}
      {isMobile && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: 78,
          paddingBottom: 'env(safe-area-inset-bottom)', background: C.bgCard,
          borderTop: `1px solid ${C.border}`, zIndex: 100,
          display: 'flex', alignItems: 'flex-start',
          padding: '8px 12px 0',
        }}>
          {[
            { id: 'home',     label: 'Aujourd\'hui', icon: Ic.home },
            { id: 'dossiers', label: 'Dossiers',     icon: Ic.folder },
            { id: 'compose',  label: null,           icon: null, compose: true },
            { id: 'team',     label: 'Agents',       icon: Ic.team },
            { id: 'emails',   label: 'Boîte',        icon: Ic.mail },
          ].map(item => {
            if (item.compose) {
              return (
                <button
                  key={item.id}
                  onClick={() => { setPendingSkill(null); setPendingContext(null); setPage('chat'); }}
                  aria-label="Nouvelle conversation"
                  style={{
                    flex: 1,
                    display: 'flex', justifyContent: 'center',
                    background: 'none', border: 'none', cursor: 'pointer',
                    paddingTop: 2,
                  }}
                >
                  <span style={{
                    width: 44, height: 44, borderRadius: 22,
                    background: C.accent, color: C.accentInk,
                    display: 'grid', placeItems: 'center',
                    boxShadow: '0 4px 12px rgba(107,31,42,0.25)',
                  }}>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3v10M3 8h10" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
              )
            }
            const active = page === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                style={{
                  flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  paddingTop: 2,
                  color: active ? C.accent : C.textMuted,
                }}
              >
                <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon}
                </div>
                <span style={{ fontFamily: F.sans, fontSize: 10, fontWeight: active ? 600 : 500 }}>
                  {item.label}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {/* Main column — corps sous la top-nav E (pleine largeur, scroll) */}
      {(() => {
        const isFullHeightPage = page === "chat";
        const mPadBot = isMobile ? 80 : 0;
        return (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            paddingBottom: mPadBot,
          }}>
            <main style={{
              flex: 1,
              overflow: isFullHeightPage ? "hidden" : "auto",
              padding: isFullHeightPage ? 0 : `0 ${isCompact ? 20 : 48}px`,
              display: isFullHeightPage ? "flex" : "block",
              flexDirection: "column",
              minHeight: 0,
            }}>
            {isFullHeightPage ? (
              <>
                {loading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ display: 'inline-flex', marginBottom: 12, animation: "pulse 1.6s ease-in-out infinite" }}>
                        <IAvocatMark size={28} />
                      </div>
                      <div style={{ fontFamily: F.mono, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted }}>
                        IAvocat<span style={{ color: C.accent }}>.ai</span> · chargement
                      </div>
                    </div>
                  </div>
                ) : (
                  <Suspense fallback={<PageFallback full />}>
                    <ChatPage
                      pendingSkill={pendingSkill}
                      pendingContext={pendingContext}
                      onSkillConsumed={() => { setPendingSkill(null); setPendingContext(null); }}
                      onBackToTeam={() => setPage("team")}
                    />
                  </Suspense>
                )}
              </>
            ) : (
              // E : conteneur centré généreux. L'accueil (home) respire large ;
              // les autres écrans gardent leur largeur actuelle (transitoire,
              // jusqu'à leur propre lot). Padding vertical généreux façon E.
              <div style={{
                maxWidth: page === 'home' ? 1080 : 960,
                margin: "0 auto",
                paddingTop: isCompact ? 22 : 0,
                paddingBottom: isMobile ? 16 : 24,
              }}>
                {loading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ display: 'inline-flex', marginBottom: 12, animation: "pulse 1.6s ease-in-out infinite" }}>
                        <IAvocatMark size={28} />
                      </div>
                      <div style={{ fontFamily: F.mono, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted }}>
                        IAvocat<span style={{ color: C.accent }}>.ai</span> · chargement
                      </div>
                    </div>
                  </div>
                ) : (
                  <Suspense fallback={<PageFallback />}>
                    {page === "team" && <TeamPage onSelectAgent={(agent) => { setPendingSkill(agent); setPage("chat"); }} />}
                    {page === "home" && <HomePage lawyer={lawyer} stats={stats} msgs={msgs} dossiers={dossiers} setPage={setPage} setPendingSkill={setPendingSkill} setPendingContext={setPendingContext} isMobile={isMobile} />}
                    {page === "dossiers" && <DossiersPage
                      dossiers={dossiers}
                      files={files}
                      lawyer={lawyer}
                      initialDossierId={dossierToOpen}
                      onConsumeInitial={() => setDossierToOpen(null)}
                      onRefresh={load}
                      onAssignToAgent={({ agent, dossier, instructions }) => {
                        const contextLines = [
                          `Contexte — dossier client :`,
                          `• Client : ${dossier.client_name || '(inconnu)'}`,
                          dossier.dossier_ref ? `• Référence : ${dossier.dossier_ref}` : '',
                          dossier.matiere ? `• Matière : ${dossier.matiere}` : '',
                          dossier.cloud_folder_id ? `• Dossier cloud : ${dossier.cloud_provider === 'onedrive' ? 'OneDrive' : 'Google Drive'} (fichiers disponibles)` : '',
                          '',
                          instructions?.trim() ? `Ma demande : ${instructions.trim()}` : `Merci de m'aider à travailler sur ce dossier.`,
                        ].filter(Boolean).join('\n');
                        setPendingSkill(agent);
                        setPendingContext({
                          type: 'dossier',
                          dossier_id: dossier.id,
                          prompt: contextLines,
                        });
                        setPage('chat');
                      }}
                      onWriteActe={(d) => { setActeContext(d); setPage('acte'); }}
                    />}
                    {page === "agenda" && <AgendaPage
                      lawyer={lawyer}
                      isMobile={isMobile}
                      dossiers={dossiers}
                      onAssignToAgent={({ agent, event, dossier, instructions }) => {
                        const dateStr = event.date?.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
                        const timeStr = event.date?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                        const contextLines = [
                          `Contexte — événement agenda :`,
                          `• Titre : ${event.summary || '(sans titre)'}`,
                          `• Date : ${dateStr}${timeStr ? ' à ' + timeStr : ''}`,
                          event.location ? `• Lieu : ${event.location}` : '',
                          dossier ? `• Dossier : ${dossier.client_name}${dossier.dossier_ref ? ' (' + dossier.dossier_ref + ')' : ''}${dossier.matiere ? ' — ' + dossier.matiere : ''}` : '• Dossier : non rattaché',
                          event.description ? `• Description : ${event.description}` : '',
                          '',
                          instructions?.trim() ? `Ma demande : ${instructions.trim()}` : `Merci de m'aider à préparer cet événement.`,
                        ].filter(Boolean).join('\n');
                        setPendingSkill(agent);
                        setPendingContext({
                          type: 'calendar',
                          event_id: event.id,
                          dossier_id: dossier?.id || null,
                          prompt: contextLines,
                        });
                        setPage('chat');
                      }}
                    />}
                    {page === "emails" && <EmailsPage lawyer={lawyer} />}
                    {page === "jurisprudence" && <JurisprudencePage
                      lawyer={lawyer}
                      dossiers={dossiers}
                      isMobile={isMobile}
                      onCiteIn={(arret) => {
                        // « Citer » / « Approfondir » : on route vers le chat avec le
                        // contexte de la décision (handler préservé). Un handoff vers un
                        // collaborateur précise l'agent à pré-sélectionner.
                        if (arret?.type === 'handoff' && arret.talkAgentId) {
                          const ag = AGENTS.find(a => a.id === arret.talkAgentId);
                          if (ag) setPendingSkill(ag);
                          setPendingContext({ type: 'jurisprudence', ref: arret.ref, title: arret.title, prompt: `Approfondis cette recherche jurisprudentielle :\n${arret.title}` });
                        } else {
                          setPendingContext({ type: 'jurisprudence', ref: arret.ref, title: arret.title, prompt: `Cite cette décision dans nos écritures :\n${arret.ref}${arret.title && arret.title !== arret.ref ? '\n' + arret.title : ''}` });
                        }
                        setPage('chat');
                      }}
                      onAttach={(arret, dossier) => {
                        // Rattacher une décision à un dossier réel → chat avec le bon
                        // collaborateur référent (dérivé de la matière du dossier).
                        const agent = agentForMatiere(dossier?.matiere) || null;
                        if (agent) setPendingSkill(agent);
                        const contextLines = [
                          `Contexte — décision à verser au dossier :`,
                          `• Référence : ${arret.ref}`,
                          arret.sommaire ? `• Sommaire : ${arret.sommaire}` : '',
                          arret.url ? `• Source : ${arret.url}` : '',
                          dossier ? `• Dossier : ${dossier.client_name}${dossier.dossier_ref ? ' (' + dossier.dossier_ref + ')' : ''}${dossier.matiere ? ' — ' + dossier.matiere : ''}` : '• Dossier : non rattaché',
                          '',
                          `Merci d'analyser la portée de cette décision pour ce dossier et de proposer comment l'exploiter dans nos écritures.`,
                        ].filter(Boolean).join('\n');
                        setPendingContext({ type: 'jurisprudence', dossier_id: dossier?.id || null, ref: arret.ref, title: arret.title, prompt: contextLines });
                        setPage('chat');
                      }}
                    />}
                    {page === "settings" && <SettingsPage lawyer={lawyer} msStatus={msStatus} googleAuthStatus={googleAuthStatus} googleAuthEmail={googleAuthEmail} />}
                    {page === "support" && <SupportPage />}
                  </Suspense>
                )}
              </div>
            )}
          </main>
          </div>
        );
      })()}

      {/* FloatingChat widget removed — chat is accessed via agent cards */}

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; }
        textarea::-webkit-scrollbar { width: 3px; }
        @media (max-width: 768px) {
          .hp-favs-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .hp-two-cols { grid-template-columns: 1fr !important; }
          .hp-two-cols .hp-separator { display: none; }
        }
      `}</style>
    </div>
  );
}
